/**
 * What happens when a tenant picks a payment method (COL-533).
 *
 * V1 encodes a rule that is easy to miss and expensive to lose: switching
 * AWAY from an active SEPA mandate is not the tenant's to do. A mandate is a
 * standing authorisation to debit their account, so ending it goes through an
 * admin-approved cancellation request — the tenant may ask, not act.
 * `Dashboard.tsx`'s `choosePayMethod` returns early in that case and changes
 * nothing.
 *
 * V2 draws the design's three-way picker over the same decision. If it
 * re-derived the rule it would eventually drift, and the drift would look
 * like a tenant quietly switching off a direct debit we still believe is
 * live. So the decision is made here, once, and each dashboard renders the
 * outcome in its own way.
 */

export type PaymentMethodChoice = "sepa_debit" | "card" | "bank_transfer";

/** The only methods a cancellation request can be raised for. */
export type NonMandateMethod = Exclude<PaymentMethodChoice, "sepa_debit">;

export type ChoiceOutcome =
  /** Persist the choice on the booking. */
  | { action: "switch"; method: PaymentMethodChoice }
  /** An active mandate stands in the way; open the cancellation request. */
  | { action: "requestCancelMandate"; method: NonMandateMethod }
  /** Same, but a request is already filed — say so instead of filing twice. */
  | { action: "cancelAlreadyRequested" };

export function resolvePaymentMethodChoice(
  method: PaymentMethodChoice,
  state: { hasActiveMandate: boolean; cancelAlreadyRequested: boolean }
): ChoiceOutcome {
  // Choosing SEPA while a mandate is active is a no-op switch, not a
  // cancellation — it is the method already in force.
  if (method !== "sepa_debit" && state.hasActiveMandate) {
    return state.cancelAlreadyRequested
      ? { action: "cancelAlreadyRequested" }
      : { action: "requestCancelMandate", method: method as NonMandateMethod };
  }
  return { action: "switch", method };
}

/**
 * Is the payment state still unknown?
 *
 * Both queries behind the picker report absence and failure as the SAME
 * value: `useEffectivePaymentMethods` yields all-rails-false, and
 * `useTenantPaymentMethod` falls back to `mandateActive: false`. So "there is
 * no mandate" and "we could not find out" are indistinguishable downstream —
 * and the two readings lead to opposite, both-wrong behaviour:
 *
 *  - treat unknown as "no mandate" and the tenant can switch away from a
 *    direct debit that is still live, pay by hand, and be debited as well;
 *  - treat unknown as "no rails" and we offer them nothing at all.
 *
 * So the caller must fold loading AND error from both queries into one
 * answer, and the UI must refuse to write while it holds. This is a function
 * rather than an inline `||` chain because the inline version shipped missing
 * three of its four terms and nothing could see it.
 */
export function isPaymentStateUnknown(state: {
  railsLoading: boolean;
  railsFailed: boolean;
  mandateLoading: boolean;
  mandateFailed: boolean;
  /** The mandate query has actually returned a row (not just "not loading"). */
  mandateLoaded: boolean;
}): boolean {
  return (
    state.railsLoading ||
    state.railsFailed ||
    state.mandateLoading ||
    state.mandateFailed ||
    !state.mandateLoaded
  );
}
